<script setup lang="ts">
// Pull requests of a repository (HRTH-115): a list, opening one from a branch, and a pull
// request's page: its conversation, its changes (with line comments), reviews, merging,
// closing and reopening. Everything is encrypted under the repository's key and worked
// out on this device (lib/pulls.ts); the repository itself is loaded by RepoBrowser.
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { describeError } from '../api/http'
import type { Pull, PullState } from '../api/types'
import type { GitFileDiff, RefTarget, Repository } from '../lib/git'
import { shortSha } from '../lib/git'
import {
  anchorOf,
  diffLines,
  encryptPullBody,
  mergePull,
  previewMerge,
  pullSummary,
  sameAnchor,
  shouldRecordPush,
  viewPull,
  type LineAnchor,
  type PullBody,
  type PullView,
  type Verdict,
} from '../lib/pulls'
import { renderMarkdown } from '../lib/render'
import type { OpenLibrary } from '../sync/engine'
import { usePulls } from '../stores/pulls'
import { useSession } from '../stores/session'

const props = defineProps<{ library: OpenLibrary; repo: Repository; refs: RefTarget[]; protectedBranches?: Set<string> }>()
const emit = defineEmits<{ changed: [] }>()
const session = useSession()
const pulls = usePulls()

const key = computed(() => session.libraryKey(props.library.id) ?? '')
const branches = computed(() => props.refs.filter((r) => r.name.startsWith('refs/heads/')))
const short = (name: string) => name.replace(/^refs\/heads\//, '')
const shaOf = (name: string) => props.refs.find((r) => r.name === name)?.sha
const nameOf = (u?: string) => (u ? (session.profiles.get(u)?.display_name ?? u) : 'someone')
const when = (ms: number) => new Date(ms).toLocaleString()

// --- List ----------------------------------------------------------------------------

type Row = { pull: Pull; title: string; source: string; target: string }
const rows = ref<Row[]>([])
const filter = ref<PullState>('open')
const open = ref<string | null>(null)
const creating = ref(false)

async function loadList(): Promise<void> {
  try {
    const out: Row[] = []
    for (const p of (await session.api.pulls(props.library.id)).pulls) {
      const s = await pullSummary(key.value, props.library.id, p)
      if (s) out.push({ pull: p, ...s })
    }
    rows.value = out
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}
watch(() => props.library.id, () => {
  open.value = null
  void loadList()
}, { immediate: true })
const shown = computed(() => rows.value.filter((r) => r.pull.state === filter.value))

// Someone else's activity on this repository's pull requests shows up as it happens.
watch(
  () => pulls.changed[props.library.id],
  () => {
    void loadList()
    if (open.value) void loadPull()
  },
)
watch(open, (id) => {
  if (!id) pulls.setViewing(null)
})
onBeforeUnmount(() => pulls.setViewing(null))

// --- Opening one ------------------------------------------------------------------------

const draft = ref({ source: '', target: '', title: '', description: '' })
function startNew(): void {
  const main = branches.value.find((b) => b.name === 'refs/heads/main') ?? branches.value[0]
  const other = branches.value.find((b) => b.name !== main?.name)
  draft.value = { source: other?.name ?? '', target: main?.name ?? '', title: other ? short(other.name) : '', description: '' }
  creating.value = true
}
async function create(): Promise<void> {
  const d = draft.value
  const head = shaOf(d.source)
  if (!head || !d.target || d.source === d.target || !d.title.trim()) return
  try {
    const body: PullBody = { type: 'opened', title: d.title.trim(), description: d.description, source: d.source, target: d.target, head }
    const created = await session.api.openPull(props.library.id, await encryptPullBody(key.value, props.library.id, body))
    creating.value = false
    await loadList()
    open.value = created.pull.id
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

// --- A pull request ---------------------------------------------------------------------

const view = ref<PullView | null>(null)
const tab = ref<'conversation' | 'changes'>('conversation')
const description = ref('')
const busy = ref(false)

async function loadPull(): Promise<void> {
  if (!open.value) return
  try {
    const detail = await session.api.pull(open.value)
    view.value = await viewPull(key.value, props.library.id, detail)
    pulls.setViewing(detail.pull.id, detail.pull.events)
    description.value = view.value?.description ? await renderMarkdown(view.value.description) : ''
    // The source branch moved since the log last recorded it: record that (anyone can).
    const live = view.value && shaOf(view.value.source)
    if (view.value?.state === 'open' && live && shouldRecordPush(props.repo, view.value.head, live)) {
      await post({ type: 'updated', head: live })
    }
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}
watch(open, () => {
  view.value = null
  tab.value = 'conversation'
  void loadPull()
})

const head = computed(() => (view.value ? (shaOf(view.value.source) ?? view.value.head) : ''))
const targetSha = computed(() => (view.value ? shaOf(view.value.target) : undefined))
const files = computed<GitFileDiff[]>(() => {
  if (!view.value || !head.value || !targetSha.value || !props.repo.has(head.value)) return []
  try {
    const base = props.repo.mergeBase(targetSha.value, head.value) ?? targetSha.value
    return props.repo.diffBetween(base, head.value)
  } catch {
    return []
  }
})
const mergeCheck = computed(() => {
  if (!view.value || view.value.state !== 'open' || !head.value || !targetSha.value || !props.repo.has(head.value)) return null
  try {
    return previewMerge(props.repo, targetSha.value, head.value)
  } catch {
    return null
  }
})
const canPush = computed(() => props.library.can_upload)
const isAuthor = computed(() => !!view.value && view.value.author === session.me)

async function post(body: PullBody, state?: PullState): Promise<void> {
  if (!open.value) return
  busy.value = true
  try {
    await session.api.addPullEvent(open.value, await encryptPullBody(key.value, props.library.id, body), state)
    await loadPull()
    void loadList()
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}

const comment = ref('')
const verdict = ref<Verdict>('comment')
async function submitComment(): Promise<void> {
  const text = comment.value.trim()
  if (!text && verdict.value === 'comment') return
  if (verdict.value === 'comment') await post({ type: 'comment', text, head: head.value })
  else await post({ type: 'review', verdict: verdict.value, text, head: head.value })
  comment.value = ''
  verdict.value = 'comment'
}

const lineDraft = ref<{ at: LineAnchor; text: string } | null>(null)
async function submitLine(): Promise<void> {
  const d = lineDraft.value
  if (!d?.text.trim()) return
  await post({ type: 'comment', text: d.text.trim(), at: d.at, head: head.value })
  lineDraft.value = null
}
const lineComments = (at: LineAnchor) =>
  view.value?.timeline.filter((t) => t.body.type === 'comment' && sameAnchor(t.body.at, at)) ?? []

async function merge(): Promise<void> {
  const v = view.value
  if (!v || !confirm(`Merge ${short(v.source)} into ${short(v.target)}?`)) return
  busy.value = true
  try {
    const me = session.me ?? 'hrth'
    const done = await mergePull(session.api, props.repo, props.library.id, key.value, v, head.value, {
      name: nameOf(me),
      email: me,
    })
    await post({ type: 'merged', ...done }, 'merged')
    emit('changed')
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}

function describe(b: PullBody): string {
  switch (b.type) {
    case 'opened':
      return 'opened this'
    case 'updated':
      return `pushed ${shortSha(b.head)}`
    case 'review':
      return b.verdict === 'approve' ? 'approved' : b.verdict === 'changes' ? 'asked for changes' : 'reviewed'
    case 'closed':
      return 'closed this'
    case 'reopened':
      return 'reopened this'
    case 'merged':
      return `merged it as ${shortSha(b.commit)}`
    default:
      return b.at ? `commented on ${b.at.path}:${b.at.line}` : 'commented'
  }
}
</script>

<template>
  <section data-testid="repo-pulls">
    <!-- List -->
    <template v-if="!open">
      <div class="mb-3 flex flex-wrap items-center gap-4">
        <button
          v-for="s in ['open', 'merged', 'closed'] as const"
          :key="s"
          class="mono uppercase"
          :class="filter === s ? 'text-text-primary' : 'text-text-muted hover:text-text-primary'"
          @click="filter = s"
        >
          {{ s }} · {{ String(rows.filter((r) => r.pull.state === s).length).padStart(2, '0') }}
        </button>
        <span class="flex-1" />
        <button class="btn-primary" :disabled="branches.length < 2" data-testid="pull-new" @click="startNew">New pull request</button>
      </div>
      <form v-if="creating" class="mb-4 space-y-3 border border-border p-4" data-testid="pull-form" @submit.prevent="create">
        <div class="flex flex-wrap items-center gap-2">
          <select v-model="draft.source" class="input w-auto" aria-label="From branch" data-testid="pull-source">
            <option v-for="b in branches" :key="b.name" :value="b.name">{{ short(b.name) }}</option>
          </select>
          <span class="mono text-text-muted">into</span>
          <select v-model="draft.target" class="input w-auto" aria-label="Into branch" data-testid="pull-target">
            <option v-for="b in branches" :key="b.name" :value="b.name">{{ short(b.name) }}</option>
          </select>
        </div>
        <input v-model="draft.title" class="input" placeholder="Title" maxlength="200" data-testid="pull-title" />
        <textarea v-model="draft.description" class="input min-h-28" placeholder="What does it change, and why? (Markdown)" />
        <div class="flex justify-end gap-2">
          <button type="button" class="btn-ghost" @click="creating = false">Cancel</button>
          <button class="btn-primary" :disabled="!draft.title.trim() || draft.source === draft.target" data-testid="pull-create">Open pull request</button>
        </div>
      </form>
      <div class="border-t border-border">
        <button
          v-for="r in shown"
          :key="r.pull.id"
          class="grid w-full grid-cols-[3rem_1fr_auto] items-baseline gap-3 border-b border-border py-2 text-left text-sm hover:bg-bg-hover"
          :data-testid="`pull-${r.pull.number}`"
          @click="open = r.pull.id"
        >
          <span class="mono text-text-muted">#{{ r.pull.number }}</span>
          <span class="min-w-0 truncate" :class="pulls.isUnread(r.pull.id) ? 'font-medium' : ''">
            <span v-if="pulls.isUnread(r.pull.id)" class="mr-1.5 inline-block h-1.5 w-1.5 bg-signal align-middle" title="New activity" data-testid="pull-unread" />{{ r.title }}
            <span class="text-text-muted">· {{ short(r.source) }} → {{ short(r.target) }}</span>
          </span>
          <span class="mono text-text-muted">{{ nameOf(r.pull.author) }}</span>
        </button>
        <p v-if="shown.length === 0" class="mono py-2 text-text-muted">No {{ filter }} pull requests.</p>
      </div>
    </template>

    <!-- One pull request -->
    <template v-else-if="view">
      <button class="mono mb-2 text-text-muted uppercase hover:text-text-primary" @click="open = null">← Pull requests</button>
      <h2 class="text-2xl font-light tracking-tight" data-testid="pull-heading">{{ view.title }} <span class="text-text-muted">#{{ view.number }}</span></h2>
      <p class="mono mt-1 text-text-muted">
        <span class="uppercase" :class="view.state === 'open' ? 'text-text-primary' : ''">{{ view.state }}</span>
        · {{ nameOf(view.author) }} wants {{ short(view.source) }} into {{ props.protectedBranches?.has(view.target) ? '🔒 ' : '' }}{{ short(view.target) }}
        <template v-for="[who, r] in view.reviews" :key="who"> · {{ nameOf(who) }} {{ r.verdict === 'approve' ? 'approved' : 'asked for changes' }}{{ r.head !== head ? ' (earlier version)' : '' }}</template>
      </p>
      <div class="mono my-3 flex gap-4 uppercase">
        <button :class="tab === 'conversation' ? 'text-text-primary' : 'text-text-muted hover:text-text-primary'" @click="tab = 'conversation'">Conversation</button>
        <button :class="tab === 'changes' ? 'text-text-primary' : 'text-text-muted hover:text-text-primary'" data-testid="pull-changes" @click="tab = 'changes'">
          Changes · {{ files.length }}
        </button>
      </div>

      <template v-if="tab === 'conversation'">
        <!-- eslint-disable-next-line vue/no-v-html -- sanitised by renderMarkdown -->
        <article v-if="description" class="md mb-4 max-w-3xl border border-border p-4" v-html="description" />
        <ol class="space-y-2" data-testid="pull-timeline">
          <li v-for="t in view.timeline.slice(1)" :key="t.id" class="border-l border-border pl-3 text-sm">
            <span class="mono text-text-muted">{{ nameOf(t.author) }} {{ describe(t.body) }} · {{ when(t.at) }}</span>
            <p v-if="(t.body.type === 'comment' || t.body.type === 'review') && t.body.text" class="mt-1 whitespace-pre-wrap">{{ t.body.text }}</p>
          </li>
        </ol>

        <!-- Merge -->
        <div v-if="view.state === 'open'" class="mt-4 border border-border p-3" data-testid="pull-merge-box">
          <p v-if="!mergeCheck" class="mono text-text-muted">Checking whether it merges…</p>
          <p v-else-if="mergeCheck.kind === 'conflicts'" class="mono text-signal">
            Both branches changed {{ mergeCheck.paths.join(', ') }}: merge locally with git, then push.
          </p>
          <p v-else-if="mergeCheck.kind === 'up_to_date'" class="mono text-text-muted">{{ short(view.target) }} already has these changes.</p>
          <div v-else class="flex flex-wrap items-center gap-3">
            <span class="mono text-text-muted">{{ mergeCheck.kind === 'fast_forward' ? 'Merges by fast-forward.' : 'Merges with a merge commit.' }}</span>
            <span class="flex-1" />
            <button v-if="canPush" class="btn-primary" :disabled="busy" data-testid="pull-merge" @click="merge">Merge</button>
            <span v-else class="mono text-text-muted">Someone who can push merges it.</span>
            <span v-if="props.protectedBranches?.has(view.target)" class="mono basis-full text-text-muted">
              🔒 {{ short(view.target) }} is protected: only its roles can merge into it.
            </span>
          </div>
        </div>

        <!-- Comment or review -->
        <form v-if="view.state !== 'merged'" class="mt-4 space-y-2" @submit.prevent="submitComment">
          <textarea v-model="comment" class="input min-h-20" placeholder="Leave a comment" data-testid="pull-comment" />
          <div class="flex flex-wrap items-center gap-3">
            <label v-for="v in ['comment', 'approve', 'changes'] as const" :key="v" class="mono inline-flex items-center gap-1.5 uppercase">
              <input v-model="verdict" type="radio" :value="v" name="verdict" />
              {{ v === 'comment' ? 'Comment' : v === 'approve' ? 'Approve' : 'Request changes' }}
            </label>
            <span class="flex-1" />
            <button
              v-if="(canPush || isAuthor) && view.state === 'open'"
              type="button"
              class="btn-ghost"
              :disabled="busy"
              @click="post({ type: 'closed' }, 'closed')"
            >
              Close
            </button>
            <button
              v-if="(canPush || isAuthor) && view.state === 'closed'"
              type="button"
              class="btn-ghost"
              :disabled="busy"
              @click="post({ type: 'reopened' }, 'open')"
            >
              Reopen
            </button>
            <button class="btn-primary" :disabled="busy" data-testid="pull-submit">{{ verdict === 'comment' ? 'Comment' : 'Submit review' }}</button>
          </div>
        </form>
      </template>

      <!-- Changes, with line comments -->
      <template v-else>
        <p v-if="!props.repo.has(head)" class="mono text-text-muted">The source branch's latest commit isn't here yet: reload the repository.</p>
        <div v-for="f in files" :key="f.path" class="mb-4 border border-border" :data-testid="`pull-diff-${f.path}`">
          <div class="mono flex items-center gap-3 border-b border-border px-3 py-1.5 text-text-muted">
            <span class="uppercase">{{ f.change }}</span>
            <span class="min-w-0 flex-1 truncate text-text-primary">{{ f.path }}</span>
            <span v-if="!f.binary">+{{ f.additions }} −{{ f.deletions }}</span>
          </div>
          <p v-if="f.binary || !f.patch" class="mono px-3 py-2 text-text-muted">{{ f.binary ? 'Binary file.' : 'Too large to show.' }}</p>
          <div v-else class="code-text scroll-thin overflow-x-auto text-[13px] leading-5">
            <template v-for="(l, i) in diffLines(f.patch)" :key="i">
              <div
                class="group flex"
                :class="l.kind === 'add' ? 'diff-add' : l.kind === 'del' ? 'diff-del' : l.kind === 'hunk' ? 'text-text-muted' : ''"
              >
                <button
                  v-if="anchorOf(f.path, l) && view.state !== 'merged'"
                  class="w-6 shrink-0 text-text-disabled opacity-0 group-hover:opacity-100 hover:text-text-primary"
                  :aria-label="`Comment on line ${l.new ?? l.old}`"
                  @click="lineDraft = { at: anchorOf(f.path, l)!, text: '' }"
                >
                  +
                </button>
                <span v-else class="w-6 shrink-0" />
                <span class="whitespace-pre px-2">{{ l.text || ' ' }}</span>
              </div>
              <template v-if="anchorOf(f.path, l)">
                <div v-for="c in lineComments(anchorOf(f.path, l)!)" :key="c.id" class="border-y border-border bg-bg-elevated px-8 py-1.5 font-sans text-sm">
                  <span class="mono text-text-muted">{{ nameOf(c.author) }} · </span>{{ c.body.type === 'comment' ? c.body.text : '' }}
                </div>
                <form
                  v-if="lineDraft && sameAnchor(lineDraft.at, anchorOf(f.path, l)!)"
                  class="flex gap-2 border-y border-border bg-bg-elevated px-8 py-2 font-sans"
                  @submit.prevent="submitLine"
                >
                  <input v-model="lineDraft.text" class="input flex-1" placeholder="Comment on this line" autofocus data-testid="pull-line-comment" />
                  <button type="button" class="btn-ghost" @click="lineDraft = null">Cancel</button>
                  <button class="btn-primary" :disabled="busy">Comment</button>
                </form>
              </template>
            </template>
          </div>
        </div>
      </template>
    </template>
  </section>
</template>
